import * as Yup from "yup";
import classNames from "classnames";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Field, Form } from "formik";
import { Modal } from "../../../molecules";
import { actions as modalActions } from "../../Modal/modal.reducers";
import {
  selectSelectedDataModel,
  deployBEApp,
} from "../../../../pages/DataModels";
import {
  DEPLOY_STATUSES,
  EMPTY_STRING,
  REGEX,
  VALIDATION_MESSAGES,
} from "../../../../constants";
import { MODAL_SIZE } from "../modals.constants";
import Spinner, { SPINNER_NAMES } from "../../Spinner";
import { showPasswordIcon, hidePasswordIcon } from "../../../../assets/icons";
import { DRAWERS } from "../../AppDrawers/drawers.constants";
import { setSelectedDrawer } from "../../Drawer";
import {
  selectSelectedApp,
  updateApplication,
} from "../../../../pages/Applications/index";
import { actions } from "../../../../pages/Applications/applications.reducers";
import { selectSelectedTab } from "../../BreadCrumb";
import { isEmpty } from "lodash";
import { selectSelectedIntegration } from "../../../../pages/Mulesoft";
import { actions as mulesoftActions } from "../../../../pages/Mulesoft/mulesoft.reducers";
import { actions as datamodalActions } from "../../../../pages/DataModels/dataModels.reducers";

const { WHITESPACES_AT_START, VERSION_FORMAT } = REGEX;
const { setSelectedModal, setShouldShowDeploymentModal } = modalActions;
const { MD } = MODAL_SIZE;
const { DEPLOY_BE_APP, UPDATE_APPLICATION } = SPINNER_NAMES;
const { FAILURE, IN_PROGRESS } = DEPLOY_STATUSES;
const { DEPLOYMENT_LOGS_DRAWER } = DRAWERS;
const { MATCH_PASSWORD, PASSWORD_REQUIRED } = VALIDATION_MESSAGES;
const VERSION_OPTIONS = ["patch", "major", "minor", "custom"];
const { setRecentlySelectedApp, setAppDeployStatus } = actions;
const { setMulesoftDeployStatus } = mulesoftActions;
const { setDeployStatus } = datamodalActions;

const DeploymentModal = ({
  setShow,
  data = {},
  show = false,
  deploymentLogs,
  isMuleSoftSection,
}) => {
  const dispatch = useDispatch();

  const selectedDataModel = useSelector(selectSelectedDataModel);
  const selectedApp = useSelector(selectSelectedApp);
  const selectedTab = useSelector(selectSelectedTab);
  const selectedIntegration = useSelector(selectSelectedIntegration);

  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isCustomOptionSelected, setIsCustomOptionSelected] = useState(false);

  const isDataModelDeployment =
    selectedTab &&
    selectedDataModel &&
    selectedDataModel.name === selectedTab.name;
  const isApplicationDeployment =
    selectedTab && selectedApp && selectedApp.name === selectedTab.name;

  const handleClose = useCallback(() => {
    setShow(false);
    dispatch(setSelectedModal(null));
  }, [dispatch, setShow]);

  const initialValues = {
    deployMessage: EMPTY_STRING,
    password: EMPTY_STRING,
    version: EMPTY_STRING,
    customVersion: EMPTY_STRING,
  };

  const validationSchema = () => {
    let baseSchema = {
      deployMessage: Yup.string().matches(
        WHITESPACES_AT_START,
        "Deploy Message must not contain white spaces at start"
      ),
      password: Yup.string()
        .oneOf([process.env.REACT_APP_DEPLOY_BE_PASSWORD], MATCH_PASSWORD)
        .required(PASSWORD_REQUIRED),
    };
    if (!isEmpty(deploymentLogs)) {
      baseSchema = {
        ...baseSchema,
        version: Yup.string().required("version is required"),
        customVersion: Yup.string().when("version", {
          is: "custom",
          then: Yup.string()
            .required("version is required")
            .matches(VERSION_FORMAT, 'Version must be in "1.0.0" format'),
          otherwise: Yup.string().notRequired(),
        }),
      };
    }
    return Yup.object(baseSchema);
  };

  const handleOnSubmit = useCallback(
    (values) => {
      const { deployMessage, version, customVersion } = values;

      const finalVersion = version === "custom" ? customVersion : version;

      if (isApplicationDeployment) {
        const { dataModels, pages, navigations, ...rest } = selectedApp;
        const updatedApp = {
          ...rest,
          pricePlan: data.pricePlan,
          applicationJson: {
            ...rest.applicationJson,
            properties: {
              ...rest.applicationJson.properties,
              ...data,
              deployMessage,
              version: finalVersion,
            },
          },
        };
        dispatch(
          updateApplication({
            id: selectedApp.id,
            app: updatedApp,
          })
        ).then((res) => {
          if (res.payload) {
            dispatch(
              setRecentlySelectedApp({ ...res.payload, redeployed: true })
            );
            handleClose();
            dispatch(setShouldShowDeploymentModal(true));
            dispatch(
              setAppDeployStatus({
                frontend: IN_PROGRESS,
                backend: IN_PROGRESS,
              })
            );
          }
        });
      }
      if (isDataModelDeployment || isMuleSoftSection) {
        const mulesoftRequest = {
          integrationId: selectedIntegration?.id,
          dataModelId: EMPTY_STRING,
          appName: selectedIntegration?.integrationRefName,
          hasExternalSource: true,
          deployMessage,
          version: finalVersion,
        };
        const datamodelRequest = {
          appName: selectedDataModel?.dbRefName,
          dataModelId: selectedDataModel?.id,
          deployMessage,
          version: finalVersion,
        };

        const requestBody = isMuleSoftSection ? mulesoftRequest : datamodelRequest;
        dispatch(deployBEApp(requestBody)).then((res) => {
          if (res.payload) {
            handleClose();
            dispatch(setSelectedDrawer(DEPLOYMENT_LOGS_DRAWER));
            dispatch(
              isMuleSoftSection
                ? setMulesoftDeployStatus(IN_PROGRESS)
                : setDeployStatus(IN_PROGRESS)
            );
          } else
            dispatch(
              isMuleSoftSection
                ? setMulesoftDeployStatus(FAILURE)
                : setDeployStatus(FAILURE)
            );
        });
      }
    },
    [
      dispatch,
      handleClose,
      selectedDataModel,
      selectedIntegration,
      selectedApp,
      isApplicationDeployment,
      isDataModelDeployment,
      isMuleSoftSection,
      data,
    ]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleOnSubmit}
      validateOnMount={true}
    >
      {({
        values,
        handleSubmit,
        handleReset,
        handleChange,
        touched,
        errors,
        isValid,
      }) => {
        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn-primary"
              type="submit"
              onClick={handleSubmit}
              disabled={isSpinnerActive || !isValid}
            >
              <Spinner
                name={[DEPLOY_BE_APP, UPDATE_APPLICATION]}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                Submit
              </Spinner>
            </button>
          </div>
        );
        return (
          <Modal
            title="Deploy Application"
            handleReset={handleReset}
            handleClose={handleClose}
            size={MD}
            footer={footer}
            shouldOpen={show}
          >
            <Form>
              <div className="p-6">
                <div className="pb-4">
                  <div>
                    <label className="text-sm font-medium text-gray-50 mb-1 block text-left">
                      Deploy Message
                    </label>
                    <div className="flex flex-between items-center bg-white rounded-md">
                      <Field
                        name="deployMessage"
                        placeholder="Deploy Message"
                        className={classNames("form-control !ring-0 border-0", {
                          "border-red-600":
                            touched.deployMessage && errors.deployMessage,
                        })}
                        type="text"
                        value={values.deployMessage}
                      />
                    </div>
                    {touched.deployMessage && errors.deployMessage && (
                      <div className="font-medium text-red-500 text-sm">
                        {errors.deployMessage}
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-50 mb-1 block text-left">
                      Enter Deploy Password
                    </label>
                    <div
                      className={classNames(
                        "flex flex-between items-center bg-white rounded-md border-2",
                        {
                          "border-red-600": touched.password && errors.password,
                        }
                      )}
                    >
                      <Field
                        type={showPassword ? "text" : "password"}
                        className="form-control !ring-0 border-0"
                        placeholder="Password"
                        name="password"
                        value={values.password}
                      />
                      <img
                        alt="show Password"
                        src={showPassword ? showPasswordIcon : hidePasswordIcon}
                        onClick={() => setShowPassword(!showPassword)}
                        className=" w-8 h-4 pr-2 cursor-pointer"
                      />
                    </div>
                    {touched.password && errors.password && (
                      <div className="font-medium text-red-500 text-sm">
                        {errors.password}
                      </div>
                    )}
                  </div>
                </div>
                {!isEmpty(deploymentLogs) && (
                  <div className="mt-3 pb-4">
                    <div>
                      <label className="text-sm font-medium text-gray-50 mb-1 block text-left">
                        Version
                      </label>
                      <div className="flex flex-between items-center bg-white rounded-md border-2">
                        <Field
                          as="select"
                          name="version"
                          className={classNames(
                            "form-control !ring-0 border-0 capitalize",
                            {
                              "border-red-600":
                                touched.version && errors.version,
                            }
                          )}
                          onClick={(e) =>
                            setIsCustomOptionSelected(
                              e.target.value === "custom"
                            )
                          }
                          onChange={handleChange}
                        >
                          <option value={EMPTY_STRING}>Select Service</option>
                          {VERSION_OPTIONS.map((value) => (
                            <option
                              key={value}
                              value={value}
                              className="bg-gray-800 py-0 pl-4 text-white"
                            >
                              {value}
                            </option>
                          ))}
                        </Field>
                      </div>
                      {touched.version && errors.version && (
                        <div className="font-medium text-red-500 text-sm">
                          {errors.version}
                        </div>
                      )}
                      {isCustomOptionSelected && (
                        <div className="mt-3 pb-4">
                          <label className="text-sm font-medium text-gray-50 mb-1 block">
                            Custom Version
                          </label>
                          <div className="flex flex-between items-center bg-white rounded-md">
                            <Field
                              type="text"
                              className={classNames(
                                "form-control !ring-0 border-0",
                                {
                                  "border-red-600":
                                    touched.customVersion &&
                                    errors.customVersion,
                                }
                              )}
                              placeholder="e.g. 1.0.0"
                              name="customVersion"
                            />
                          </div>
                          {touched.customVersion && errors.customVersion && (
                            <div className="font-medium text-red-500 text-sm">
                              {errors.customVersion}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default DeploymentModal;
